/**
 * CollectionRecordDetail — Full detail page for a single collection record.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Button, Tag, Spin, Divider, Input, message,
} from "antd";
import {
	ArrowLeftOutlined,
	CheckCircleOutlined,
	FlagOutlined,
} from "@ant-design/icons";
import {
	getRecordById,
	updateRecordData,
	reviewRecord,
	type CollectionRecord,
} from "@/services/data-collection-service";

const { TextArea } = Input;

const CollectionRecordDetail = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [record, setRecord] = useState<CollectionRecord | null>(null);
	const [loading, setLoading] = useState(true);
	const [editMode, setEditMode] = useState(false);
	const [editData, setEditData] = useState<Record<string, unknown>>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		getRecordById(parseInt(id))
			.then((r) => {
				setRecord(r);
				setEditData(r.finalData ?? {});
			})
			.catch(() => setRecord(null))
			.finally(() => setLoading(false));
	}, [id]);

	const handleSave = async () => {
		if (!record) return;
		setSaving(true);
		try {
			const updated = await updateRecordData(record.id, { finalData: editData });
			setRecord(updated);
			setEditMode(false);
			message.success("Record updated");
		} catch {
			message.error("Failed to update");
		}
		setSaving(false);
	};

	const handleReview = async () => {
		if (!record) return;
		try {
			const updated = await reviewRecord(record.id);
			setRecord(updated);
			message.success("Marked as reviewed");
		} catch {
			message.error("Failed to review");
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "COMPLETED": return "success";
			case "REVIEWED": return "blue";
			case "PROCESSING": return "processing";
			case "FLAGGED": return "error";
			default: return "default";
		}
	};

	if (loading) return <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>;
	if (!record) return <div style={{ padding: 24 }}>Record not found</div>;

	const data = editMode ? editData : (record.finalData ?? record.aiExtractedData ?? {});

	return (
		<div style={{ padding: 24, maxWidth: 800 }}>
			<Button
				type="text"
				icon={<ArrowLeftOutlined />}
				onClick={() => navigate("/data-collection")}
				style={{ marginBottom: 16 }}
			>
				Back to Records
			</Button>

			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
				<div>
					<h2 style={{ margin: 0 }}>{record.templateName || "Collection Record"}</h2>
					<div style={{ display: "flex", gap: 8, marginTop: 6 }}>
						<Tag color={getStatusColor(record.status)}>{record.status}</Tag>
						{record.templateCategory && <Tag color="purple">{record.templateCategory}</Tag>}
						{record.areaName && <Tag>{record.areaName}</Tag>}
					</div>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					{record.status === "COMPLETED" && (
						<Button icon={<CheckCircleOutlined />} onClick={handleReview}>
							Mark Reviewed
						</Button>
					)}
					{!editMode ? (
						<Button type="primary" onClick={() => setEditMode(true)}>Edit Data</Button>
					) : (
						<>
							<Button onClick={() => { setEditMode(false); setEditData(record.finalData ?? {}); }}>Cancel</Button>
							<Button type="primary" onClick={handleSave} loading={saving}>Save</Button>
						</>
					)}
				</div>
			</div>

			{/* Photo */}
			{record.primaryPhotoUrl && (
				<div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 20, maxHeight: 300 }}>
					<img src={record.primaryPhotoUrl} alt="Collection" style={{ width: "100%", maxHeight: 300, objectFit: "cover" }} />
				</div>
			)}

			{/* Metadata */}
			<div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>
				{record.locationName && <span>Location: <strong>{record.locationName}</strong></span>}
				{record.collectedByName && <span>By: <strong>{record.collectedByName}</strong></span>}
				<span>Date: <strong>{new Date(record.createdAt).toLocaleString()}</strong></span>
				{record.healthScoreContribution !== null && record.healthScoreContribution !== undefined && (
					<span>
						Health: <strong style={{ color: record.healthScoreContribution >= 80 ? "#52c41a" : record.healthScoreContribution >= 50 ? "#faad14" : "#ff4d4f" }}>
							{Math.round(record.healthScoreContribution)}
						</strong>
					</span>
				)}
			</div>

			<Divider style={{ borderColor: "rgba(255,255,255,0.1)" }} />

			{/* Field Data */}
			<h3>Collected Data</h3>
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				{Object.entries(data).map(([key, val]) => {
					const displayValue = typeof val === "object" && val !== null && "value" in (val as any)
						? (val as any).value
						: val;

					return (
						<div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
							<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
								{key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
							</span>
							{editMode ? (
								<Input
									size="small"
									value={String(editData[key] ?? "")}
									onChange={(e) => setEditData({ ...editData, [key]: e.target.value })}
									style={{ width: 200, textAlign: "right" }}
								/>
							) : (
								<span style={{ fontSize: 13, fontWeight: 500 }}>
									{displayValue === true ? "Yes" : displayValue === false ? "No" :
										Array.isArray(displayValue) ? displayValue.join(", ") :
											String(displayValue ?? "—")}
								</span>
							)}
						</div>
					);
				})}
			</div>

			{/* Notes */}
			{record.notes && (
				<>
					<Divider style={{ borderColor: "rgba(255,255,255,0.1)" }} />
					<h3>Notes</h3>
					<p style={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>{record.notes}</p>
				</>
			)}

			{/* Additional photos */}
			{record.photos && record.photos.length > 0 && (
				<>
					<Divider style={{ borderColor: "rgba(255,255,255,0.1)" }} />
					<h3>Photos</h3>
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
						{record.photos.map((p) => (
							<div key={p.id} style={{ borderRadius: 8, overflow: "hidden" }}>
								<img src={p.photoUrl} alt={p.caption || "Photo"} style={{ width: "100%", height: 120, objectFit: "cover" }} />
								{p.caption && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", padding: 4, textAlign: "center" }}>{p.caption}</div>}
							</div>
						))}
					</div>
				</>
			)}
		</div>
	);
};

export default CollectionRecordDetail;
